import { Checkbox, DefaultButton, Panel, Slider, SpinButton, TextField, Modal, IconButton, Dropdown, IDropdownOption } from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
import logo from "../../assets/logo-noglow.png";
import forbidden_logo from "../../assets/alexandria_inspector_1.png";
import readNDJSONStream from "ndjson-readablestream";

import styles from "./ChatAgent.module.css";

import {
    chatAgentApiV1,
    ChatAppRequest,
    ChatAppResponse,
    ChatAppResponseOrError,
    configApiV1,
    GPT4VInput,
    ResponseMessage,
    RetrievalMode,
    VectorFieldOptions,
    ApiError,
    loadPowerplantConfigV2,
    PowerplantInfo
} from "../../api";
import { KnowledgeSource } from "../../components/FilterSettings";
import { useGetToken, useIsLoggedIn } from "../../authConfig";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { ClearChatButton } from "../../components/ClearChatButton";
import { ExampleList } from "../../components/Example";
import { FilterPowerplantSettings } from "../../components/FilterPowerplantSettings";
import { FilterSettings } from "../../components/FilterSettings";
import { QuestionInput } from "../../components/QuestionInput";
import { SettingsButton } from "../../components/SettingsButton";
import { UserChatMessage } from "../../components/UserChatMessage";
import { Spinner } from "../../components/Spinner";
import { usePowerPlant } from "../../components/powerplant";
import { PowerplantModal } from "../../components/PowerplantModal";
import { ChangePowerplantButton } from "../../components/ChangePowerplantButton";
import React from "react";

interface ChatProps {
    gdprAccepted?: boolean;
}

const ChatAgent: React.FC<ChatProps> = ({ gdprAccepted = false }) => {
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0.3);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [retrieveCount, setRetrieveCount] = useState<number>(10);
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [filters, setFilters] = useState<{ name: string; displayName: string; values: { text: string; key: string }[] }[]>([]);
    const [exampleQuestions, setExampleQuestions] = useState<string[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [vectorFieldList, setVectorFieldList] = useState<VectorFieldOptions[]>([VectorFieldOptions.Both]);
    const [useOidSecurityFilter, setUseOidSecurityFilter] = useState<boolean>(false);
    const [useGroupsSecurityFilter, setUseGroupsSecurityFilter] = useState<boolean>(false);
    const [gpt4vInput, setGPT4VInput] = useState<GPT4VInput>(GPT4VInput.TextAndImages);
    const [useGPT4V, setUseGPT4V] = useState<boolean>(true);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);

    const [availableKnowledgeSources, setAvailableKnowledgeSources] = useState<KnowledgeSource[]>([]);
    const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSource[]>(["sharepoint"]);

    const previousPowerplantRef = useRef<string | null>(null);
    const [isPowerplantLoading, setisPowerplantLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState<null | string>(null);
    const [configLoaded, setConfigLoaded] = useState(false);
    const correlationId = useRef<string | null>(null);
    const [currentPlantImageUrl, setCurrentPlantImageUrl] = useState<string | undefined>(undefined);
    const [imageLoading, setImageLoading] = useState<boolean>(false);
    const [imageError, setImageError] = useState<boolean>(false);
    const [showPowerplantModal, setShowPowerplantModal] = useState<boolean>(false);

    const { powerplants, setPowerplants, selectedPlantId, setSelectedPlantId, selectedPlantDetails, setSelectedPlantDetails } = usePowerPlant();

    // This is needed to resize filter panel when the commands container changes size, e.g. on different screens
    const commandsContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [commandsHeight, setCommandsHeight] = React.useState(0);

    const discardKeys = ["elisabot", "riskanalysis"];
    // Measure the height of the commands container dynamically
    React.useLayoutEffect(() => {
        function updateHeight() {
            if (commandsContainerRef.current) {
                setCommandsHeight(commandsContainerRef.current.offsetHeight);
            }
        }

        updateHeight(); // measure on mount
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    const getConfig = async () => {
        setConfigLoading(true);
        setConfigError(null);

        try {
            const config = await configApiV1(token);
            console.log(config);

            const normalizedPowerplants: Record<string, PowerplantInfo> = {};
            Object.entries(config.powerplants ?? {}).forEach(([key, value]) => {
                if (discardKeys.includes(key)) {
                    return;
                }
                if (typeof value === "string") {
                    normalizedPowerplants[key] = { id: value };
                } else {
                    normalizedPowerplants[key] = value as PowerplantInfo;
                }
            });

            setPowerplants(normalizedPowerplants);
            console.log("Normalized powerplants:", normalizedPowerplants);

            setConfigLoaded(true);
        } catch (err: any) {
            if (err instanceof ApiError) {
                if (err.status === 403) {
                    setConfigError("You are not authorized to use this service. Please contact alexandria.gen@engie.com.");
                } else {
                    setConfigError("Failed to load configuration.");
                }
            } else {
                setConfigError("Unexpected error.");
            }
        } finally {
            setConfigLoading(false);
        }
    };

    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                    };
                    setStreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["choices"] && event["choices"][0]["context"] && event["choices"][0]["context"]["data_points"]) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["choices"][0]["delta"]["content"]);
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    askResponse.choices[0].context = { ...askResponse.choices[0].context, ...event["choices"][0]["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
        };
        return fullResponse;
    };

    const token = useGetToken();
    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                stream: shouldStream,
                context: {
                    callerApp: `alexandria_ui`,
                    site: selectedPlantId ?? undefined,
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        top: retrieveCount,
                        temperature: temperature,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        vector_fields: vectorFieldList,
                        use_gpt4v: useGPT4V,
                        gpt4v_input: gpt4vInput,
                        filter_powerplant: !selectedPlantId || selectedPlantId.length === 0 ? undefined : selectedPlantId.toLocaleLowerCase(),
                        filters: selectedFilters
                    },
                    knowledge_source: knowledgeSources
                },
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
            };

            const apiFunction = chatAgentApiV1;
            const response = await apiFunction(request, token, correlationId.current);
            console.log(response);
            correlationId.current = response.headers.get("X-Correlation-ID");
            if (!response.body) {
                throw Error("No response body");
            }
            if (shouldStream) {
                const parsedResponse: ChatAppResponse = await handleAsyncRequest(question, answers, response.body);
                setAnswers([...answers, [question, parsedResponse]]);
            } else {
                const parsedResponse: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    throw Error(parsedResponse.error || "Unknown error");
                }
                setAnswers([...answers, [question, parsedResponse as ChatAppResponse]]);
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
        correlationId.current = null;
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);
    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
    }, [streamedAnswers]);
    useEffect(() => {
        getConfig();
    }, []);

    useEffect(() => {
        setShowPowerplantModal(true);
    }, []);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onTemperatureChange = (
        newValue: number,
        range?: [number, number],
        event?: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent | React.KeyboardEvent
    ) => {
        setTemperature(newValue);
    };

    const onMinimumSearchScoreChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setMinimumSearchScore(parseFloat(newValue || "0"));
    };

    const onMinimumRerankerScoreChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setMinimumRerankerScore(parseFloat(newValue || "0"));
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "10"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onShouldStreamChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setShouldStream(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onUseOidSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseOidSecurityFilter(!!checked);
    };

    const onUseGroupsSecurityFilterChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseGroupsSecurityFilter(!!checked);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const notLoggedIn = !useIsLoggedIn();
    const [reloadPowerplantReloadUiTrigger, setPowerplantUiReloadTrigger] = useState(0); // Add this

    useEffect(() => {
        if (selectedPlantId) {
            const loadPowerplantUI = async () => {
                try {
                    setisPowerplantLoading(true);
                    if (setSelectedPlantDetails) {
                        setSelectedPlantDetails({
                            id: "",
                            name: "",
                            imageUrl: undefined,
                            sharepointLibraryUrl: "",
                            exampleQuestions: []
                        });
                    }

                    const plantInfo = powerplants[selectedPlantId];
                    console.log("Selected plant info:", plantInfo);
                    if (plantInfo?.imageUrl) {
                        setImageLoading(true);
                        setImageError(false);
                        setCurrentPlantImageUrl(plantInfo.imageUrl);
                    } else {
                        setCurrentPlantImageUrl(undefined);
                        setImageLoading(false);
                    }

                    const powerplantConfig = await loadPowerplantConfigV2(selectedPlantId, token);
                    console.log("Loaded powerplant config:", powerplantConfig);

                    const imageUrl = powerplantConfig.imageUrl;
                    if (imageUrl) {
                        setImageLoading(true);
                        setCurrentPlantImageUrl(imageUrl);
                    }

                    if (setSelectedPlantDetails) {
                        setSelectedPlantDetails({
                            id: powerplantConfig.id,
                            name: powerplantConfig?.name || "",
                            imageUrl: plantInfo?.imageUrl,
                            sharepointLibraryUrl: powerplantConfig.sharepointLibraryUrl || "",
                            exampleQuestions: powerplantConfig.exampleQuestions || []
                        });
                    }
                    console.log(selectedPlantDetails);

                    setFilters([]);
                    setSelectedFilters({});
                    setAvailableKnowledgeSources(powerplantConfig.knowledgeSources);
                    setKnowledgeSources(powerplantConfig.knowledgeSources);

                    // Load new filters
                    const filters: { name: string; displayName: string; values: { text: string; key: string }[] }[] = powerplantConfig.uiFields;
                    setFilters(filters);

                    const updatedSelections: Record<string, string[]> = {};
                    filters.forEach(filter => {
                        updatedSelections[filter.name] = [];
                    });
                    setSelectedFilters(updatedSelections);

                    previousPowerplantRef.current = selectedPlantId;
                    setExampleQuestions(powerplantConfig.exampleQuestions);
                } catch (error) {
                    console.error("Error fetching powerplant configuration:", error);
                } finally {
                    setisPowerplantLoading(false);
                    setShowPowerplantModal(false);
                }
            };
            loadPowerplantUI();
        }
    }, [selectedPlantId, powerplants, reloadPowerplantReloadUiTrigger]);

    const onSettingsClick = () => {
        setIsConfigPanelOpen(prev => !prev);
    };

    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
        setCurrentPlantImageUrl(undefined);
    };

    const handleChangePowerplant = () => {
        clearChat();
        setShowPowerplantModal(true);
    };

    const handlePowerplantSelect = (plantId: string) => {
        if (plantId == selectedPlantId) {
            setPowerplantUiReloadTrigger(prev => prev + 1);
            return;
        }

        setSelectedPlantId(plantId);
    };

    // const powerplantOptions: IDropdownOption[] = Object.entries(powerplants).map(([id, plantInfo]) => ({
    //     key: id,
    //     text: plantInfo.id || id
    // }));
    const activeFilterCount = Object.values(selectedFilters).filter(values => Array.isArray(values) && values.length > 0).length;

    return (
        <div className={styles.container}>
            <div className={styles.commandsContainer} ref={commandsContainerRef}>
                <ClearChatButton className={styles.commandButton} onClick={clearChat} disabled={!lastQuestionRef.current || isLoading} />
                <SettingsButton className={styles.commandButton} onClick={onSettingsClick} title="Filters" activeCount={activeFilterCount} />
                <ChangePowerplantButton className={styles.commandButton} onClick={handleChangePowerplant} title="Exit" />
            </div>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={`${styles.chatEmptyState} ${currentPlantImageUrl ? styles.hasImage : styles.noImage}`}>
                            {currentPlantImageUrl && !imageError && (
                                <div className={styles.powerplantImageContainer}>
                                    {imageLoading && <div className={styles.powerplantImageSkeleton} />}
                                    <img
                                        src={currentPlantImageUrl}
                                        alt="Powerplant"
                                        style={{ display: imageLoading ? "none" : "block" }}
                                        onLoad={handleImageLoad}
                                        onError={handleImageError}
                                    />
                                </div>
                            )}

                            <h1 className={styles.chatEmptyStateTitle}>AlexandrIA{selectedPlantDetails?.name && <> – {selectedPlantDetails.name}</>}</h1>
                            <div style={{ transform: "scale(3.5)", paddingTop: "10px", paddingBottom: "10px" }}>
                                {configError ? (
                                    <img src={forbidden_logo} alt="Chat logo" aria-hidden="true" style={{ width: "44px", height: "44px" }} />
                                ) : (
                                    <img src={logo} alt="Chat logo" aria-hidden="true" style={{ width: "22px", height: "22px" }} />
                                )}
                            </div>
                            {configLoading ? (
                                <Spinner label="Loading configuration..." />
                            ) : configError ? (
                                <div style={{ color: "red", padding: "20px", fontWeight: "bold" }}>{configError}</div>
                            ) : (
                                <ExampleList onExampleClicked={onExampleClicked} exampleQuestions={exampleQuestions} />
                            )}
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                isSelected={false}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answer[1]}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {isLoading && (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                    <div className={styles.chatInput}>
                        {configLoading ? null : configError ? null : (
                            <QuestionInput clearOnSend placeholder="Type a new question" disabled={isLoading} onSend={question => makeApiRequest(question)} />
                        )}
                    </div>
                </div>

                {answers.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers[selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}
                <PowerplantModal
                    showPowerplantModal={showPowerplantModal}
                    configLoading={configLoading}
                    selectedPlantId={selectedPlantId}
                    powerplants={powerplants}
                    handlePowerplantSelect={handlePowerplantSelect}
                    isPowerplantLoading={isPowerplantLoading}
                />
                <Panel
                    headerText="Filters"
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    // onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                    onRenderFooterContent={() => (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "8px 0" }}>
                            <button
                                onClick={() => {
                                    const clearedFilters: Record<string, string[]> = {};
                                    filters.forEach(filter => {
                                        clearedFilters[filter.name] = [];
                                    });
                                    setSelectedFilters(clearedFilters);
                                    setKnowledgeSources(["sharepoint"]);
                                }}
                                style={{
                                    fontFamily: "monospace",
                                    padding: "10px 20px",
                                    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                    border: "none",
                                    borderRadius: "8px",
                                    color: "white",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 8px rgba(240, 147, 251, 0.3)"
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(240, 147, 251, 0.4)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(240, 147, 251, 0.3)";
                                }}
                            >
                                Clear Filters
                            </button>
                            <button
                                onClick={() => setIsConfigPanelOpen(false)}
                                style={{
                                    fontFamily: "monospace",
                                    padding: "10px 20px",
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    border: "none",
                                    borderRadius: "8px",
                                    color: "white",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.4)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(102, 126, 234, 0.3)";
                                }}
                            >
                                Close
                            </button>
                        </div>
                    )}
                    isFooterAtBottom={true}
                    styles={{
                        main: {
                            top: commandsHeight * 3 // dynamically measured height of the buttons
                        },
                        headerText: {
                            fontFamily: "monospace"
                        }
                    }}
                >
                    {false && (
                        <TextField
                            className={styles.chatSettingsSeparator}
                            defaultValue={promptTemplate}
                            label="Override prompt template"
                            multiline
                            autoAdjustHeight
                            onChange={onPromptTemplateChange}
                        />
                    )}
                    {false && (
                        <Slider
                            className={styles.chatSettingsSeparator}
                            label="Temperature"
                            min={0}
                            max={1}
                            step={0.1}
                            defaultValue={temperature}
                            onChange={onTemperatureChange}
                            showValue
                            snapToStep
                        />
                    )}
                    {false && (
                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Minimum search score"
                            min={0}
                            step={0.01}
                            defaultValue={minimumSearchScore.toString()}
                            onChange={onMinimumSearchScoreChange}
                        />
                    )}
                    {false && (
                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Minimum reranker score"
                            min={1}
                            max={4}
                            step={0.1}
                            defaultValue={minimumRerankerScore.toString()}
                            onChange={onMinimumRerankerScoreChange}
                        />
                    )}
                    {false && (
                        <SpinButton
                            className={styles.chatSettingsSeparator}
                            label="Retrieve this many search results:"
                            min={1}
                            max={50}
                            defaultValue={retrieveCount.toString()}
                            onChange={onRetrieveCountChange}
                        />
                    )}
                    {false && <TextField className={styles.chatSettingsSeparator} label="Exclude category" onChange={onExcludeCategoryChanged} />}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSemanticCaptions}
                            label="Use query-contextual summaries instead of whole documents"
                            onChange={onUseSemanticCaptionsChange}
                            disabled={!useSemanticRanker}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useSuggestFollowupQuestions}
                            label="Suggest follow-up questions"
                            onChange={onUseSuggestFollowupQuestionsChange}
                        />
                    )}
                    {false && (
                        <FilterPowerplantSettings
                            filterPowerplantInputs={selectedPlantId || ""}
                            updateFilterPowerplant={inputs => {
                                setSelectedPlantId(inputs);
                            }}
                            powerplants={powerplants}
                            label="Select Powerplant"
                        />
                    )}
                    {isPowerplantLoading ? (
                        <Spinner label="Loading filters..." />
                    ) : (
                        <FilterSettings
                            filters={filters}
                            selectedFilters={selectedFilters}
                            updateFilters={setSelectedFilters}
                            knowledgeSources={availableKnowledgeSources}
                            selectedKnowledgeSources={knowledgeSources}
                            onKnowledgeSourcesChange={setKnowledgeSources}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useOidSecurityFilter}
                            label="Use oid security filter"
                            disabled={notLoggedIn}
                            onChange={onUseOidSecurityFilterChange}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={useGroupsSecurityFilter}
                            label="Use groups security filter"
                            disabled={notLoggedIn}
                            onChange={onUseGroupsSecurityFilterChange}
                        />
                    )}
                    {false && (
                        <Checkbox
                            className={styles.chatSettingsSeparator}
                            checked={shouldStream}
                            label="Stream chat completion responses"
                            onChange={onShouldStreamChange}
                        />
                    )}
                </Panel>
            </div>
        </div>
    );
};

export default ChatAgent;
